import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { AdminNav } from "@/components/admin/AdminNav";

type AuditRow = {
  id: string;
  email: string;
  ip: string | null;
  user_agent: string | null;
  event: string;
  created_at: string;
};

export const Route = createFileRoute("/admin/audit")({
  head: () => ({
    meta: [
      { title: "Admin | Login audit | Scott Cunningham" },
      { name: "description", content: "Login audit for Scott Cunningham’s website administration." },
      { property: "og:title", content: "Admin | Login audit | Scott Cunningham" },
      { property: "og:description", content: "Login audit for Scott Cunningham’s website administration." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Admin | Login audit | Scott Cunningham" },
      { name: "twitter:description", content: "Login audit for Scott Cunningham’s website administration." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AuditPage,
});

function AuditPage() {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) navigate({ to: "/login" });
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!isAdmin) return;
    setBusy(true);
    supabase
      .from("admin_login_audit")
      .select("id, email, ip, user_agent, event, created_at")
      .order("created_at", { ascending: false })
      .limit(200)
      .then(({ data }) => {
        setRows((data as AuditRow[]) ?? []);
        setBusy(false);
      });
  }, [isAdmin]);

  if (loading) return <div className="p-12 text-muted-foreground">Loading…</div>;
  if (!user) return null;
  if (!isAdmin) return <div className="p-12 text-muted-foreground">Not authorized.</div>;

  return (
    <div className="mx-auto max-w-5xl px-6 py-16">
      <AdminNav title="Login audit" />
      <p className="mt-2 text-sm text-muted-foreground">
        Latest 200 admin login events. Each successful sign-in, password verification, magic link
        send, failed attempt, and rate-limit hit is recorded.
      </p>

      <div className="mt-8 overflow-hidden rounded-md border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="px-4 py-3">When</th>
              <th className="px-4 py-3">Event</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">IP</th>
              <th className="px-4 py-3">User agent</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {busy && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {!busy && rows.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-muted-foreground">
                  No events yet.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="align-top">
                <td className="px-4 py-3 whitespace-nowrap">
                  {new Date(r.created_at).toLocaleString()}
                </td>
                <td className="px-4 py-3 font-mono text-xs">{r.event}</td>
                <td className="px-4 py-3">{r.email}</td>
                <td className="px-4 py-3 font-mono text-xs">{r.ip ?? "N/A"}</td>
                <td
                  className="px-4 py-3 text-xs text-muted-foreground max-w-[24rem] truncate"
                  title={r.user_agent ?? ""}
                >
                  {r.user_agent ?? "N/A"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
